/**
 * A caixa "Perfil: <nome>" do favorecido.
 *
 * Papel: reproduz `#modal-fav` (l. 1151–1280) — Nome somente leitura, CPF com
 * máscara, a caixa da conta apartada, a nota dos padrões, Categoria e
 * Subcategoria padrão, Forma de pagamento padrão, e, conforme a categoria
 * padrão, as ajudas de custo com os totais de 30 dias e a recorrência
 * ("Despesas com Pessoal") ou a diarista ("Petrella") — e grava o perfil
 * (`saveFavModal`, l. 2096–2163).
 *
 * O cartão "Definir como colaborador" não tem campo próprio no favorecido: ele
 * está marcado quando a categoria padrão é `CATEGORIA_PESSOAL`, marcá-lo grava
 * essa categoria e desmarcá-lo a esvazia — e, como o `perfilDoFavorecidoSchema`
 * apaga a ajuda de custo e a recorrência de quem não está em `CATEGORIA_PESSOAL`,
 * desmarcar e salvar zera as ajudas que o colaborador tinha.
 *
 * Como usar: quem monta passa o favorecido e o plano de contas, e recebe em
 * `onSalvo` o favorecido gravado, depois do aviso "✓ Perfil salvo" — é por
 * ali que o modal de lançamento reaplica o autopreenchimento. O favorecido não
 * se renomeia. Trocar a categoria padrão esvazia a subcategoria e mostra ou
 * esconde as seções de ajuda de custo e de diarista; as limpezas que a origem
 * faz ao salvar são as do `perfilDoFavorecidoSchema`, aplicadas aqui e na API.
 * Depois de gravar, pede a recorrência automática de ajuda de custo com
 * `forcar`, que gera na hora os meses da recorrência que o perfil acabou de
 * ligar e avisa por cima do "✓ Perfil salvo" quando gera algum.
 *
 * Os campos de valor das ajudas, o mês e o ano da recorrência e o valor da
 * diarista não estão dentro de `.field` na origem e saem com o contorno do
 * navegador, e não com o do formulário.
 */
import {
  CATEGORIA_PESSOAL,
  CATEGORIA_PETRELLA,
  type Categoria,
  FORMA_PAGAMENTO_LANCAMENTO,
  type Favorecido,
  type FormaPagamentoLancamento,
  ROTULOS_FORMA,
  type TipoItemAjudaCusto,
  perfilDoFavorecidoSchema,
} from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { mascararCentavos, mascararCpf, mascararDecimalLivre } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  type ValoresDoPerfil,
  corpoDoPerfil,
  totaisDoFormulario,
  valoresDoFavorecido,
} from '../formulario-do-perfil';
import { useSalvarPerfil } from '../hooks/usar-financas';
import { useGerarRecorrencia } from '../hooks/use-recorrencia';
import { NOMES_DOS_MESES, ROTULOS_DA_AJUDA_DE_CUSTO } from '../rotulos';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import {
  CLASSE_DE_SOMENTE_LEITURA,
  CLASSE_DO_CONTROLE,
  CLASSE_DO_SELECT,
  Campo,
  LinhaDeCampos,
} from './kit/campo';

/** A caixa de marcar das seções do perfil, no tamanho da origem. */
const CLASSE_DA_CAIXA = 'h-[15px] w-auto shrink-0 cursor-pointer';

/**
 * O campo de valor de uma linha, com o contorno, a fonte e as cores do
 * navegador, inclusive as de desabilitado; o recuo, o tamanho e a meia
 * opacidade da linha desmarcada são da origem.
 */
const CLASSE_DO_VALOR =
  'w-[90px] shrink-0 [border:revert] [border-radius:revert] [font-family:revert] [color:revert] [background-color:revert] px-2 py-[5px] text-right text-[12.5px] disabled:opacity-50';

/** O mês e o ano da recorrência, fora de `.field`: tudo do navegador. */
const CLASSE_DO_CAMPO_NATIVO =
  '[border:revert] [border-radius:revert] [font:revert] [color:revert] [background-color:revert]';

/** O rótulo de uma seção do perfil, como "Ajudas de custo habilitadas". */
const CLASSE_DO_ROTULO_DA_SECAO =
  'mb-1.5 block text-[11px] font-bold tracking-[0.03em] text-texto-fraco uppercase';

/** As quatro linhas de ajuda de custo, na ordem da origem, e a máscara de cada uma. */
const LINHAS_DA_AJUDA: readonly {
  tipo: TipoItemAjudaCusto;
  unidade: string;
  mascarar: (texto: string) => string;
}[] = [
  { tipo: 'custo', unidade: '/ dia', mascarar: mascararDecimalLivre },
  { tipo: 'diarias', unidade: '/ dia', mascarar: mascararDecimalLivre },
  { tipo: 'medica', unidade: 'único', mascarar: mascararCentavos },
  { tipo: 'inss', unidade: 'único', mascarar: mascararCentavos },
];

interface ModalDoPerfilProps {
  favorecido: Favorecido;
  /** O plano de contas, para as duas listas de padrão. */
  plano: readonly Categoria[];
  onFechar: () => void;
  /** Recebe o favorecido como ficou gravado. */
  onSalvo: (favorecido: Favorecido) => void;
}

/**
 * A forma escolhida, ou vazio para "— Nenhuma —".
 *
 * @param valor - O `value` da opção.
 * @returns A forma do contrato, ou vazio.
 */
function formaEscolhida(valor: string): FormaPagamentoLancamento | '' {
  return FORMA_PAGAMENTO_LANCAMENTO.find((forma) => forma === valor) ?? '';
}

/**
 * A caixa do perfil.
 *
 * @param favorecido - O favorecido aberto.
 * @param plano - O plano de contas.
 * @param onFechar - Fecha a caixa, por "✕", "Cancelar", véu ou `Esc`.
 * @param onSalvo - Recebe o favorecido gravado.
 * @returns A caixa de 460 px.
 */
export function ModalDoPerfil({ favorecido, plano, onFechar, onSalvo }: ModalDoPerfilProps) {
  const notificar = useNotificacao();
  const salvar = useSalvarPerfil();
  const gerarRecorrencia = useGerarRecorrencia();
  const [valores, setValores] = useState<ValoresDoPerfil>(() =>
    valoresDoFavorecido(favorecido, hojeEmIso()),
  );

  /**
   * Troca alguns campos.
   *
   * @param mudanca - Os campos que mudaram.
   */
  function mudar(mudanca: Partial<ValoresDoPerfil>) {
    setValores((anteriores) => ({ ...anteriores, ...mudanca }));
  }

  /**
   * Troca uma linha de ajuda de custo.
   *
   * @param tipo - A linha.
   * @param mudanca - A caixa, o valor, ou os dois.
   */
  function mudarAjuda(
    tipo: TipoItemAjudaCusto,
    mudanca: Partial<ValoresDoPerfil['ajuda'][TipoItemAjudaCusto]>,
  ) {
    setValores((anteriores) => ({
      ...anteriores,
      ajuda: { ...anteriores.ajuda, [tipo]: { ...anteriores.ajuda[tipo], ...mudanca } },
    }));
  }

  /** Valida o perfil pelo schema compartilhado e grava. */
  async function handleSalvar() {
    const perfil = perfilDoFavorecidoSchema.safeParse(corpoDoPerfil(valores));
    if (!perfil.success) {
      notificar(perfil.error.issues[0]?.message ?? 'Perfil inválido.', false);
      return;
    }
    try {
      const gravado = await salvar.mutateAsync({ id: favorecido.id, perfil: perfil.data });
      notificar('✓ Perfil salvo');
      onFechar();
      onSalvo(gravado);
      void gerarRecorrencia(gravado.residencia.id, true);
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    }
  }

  const subcategorias =
    plano.find((categoria) => categoria.nome === valores.categoriaPadrao)?.subcategorias ?? [];
  const totais = totaisDoFormulario(valores.ajuda);

  return (
    <CaixaDeDialogo
      titulo={`Perfil: ${favorecido.nome}`}
      largura={460}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleSalvar()} disabled={salvar.isPending}>
            Salvar Perfil
          </Botao>
        </>
      }
    >
      <Campo rotulo="Nome" id="perfil-nome">
        <input
          id="perfil-nome"
          readOnly
          value={favorecido.nome}
          className={cn(CLASSE_DO_CONTROLE, CLASSE_DE_SOMENTE_LEITURA)}
        />
      </Campo>
      <Campo rotulo="CPF" id="perfil-cpf">
        <input
          id="perfil-cpf"
          placeholder="000.000.000-00"
          value={valores.cpf}
          onChange={(evento) => mudar({ cpf: mascararCpf(evento.target.value) })}
          className={CLASSE_DO_CONTROLE}
        />
      </Campo>

      <div className="mb-3 rounded-[6px] border border-brass/25 bg-background px-3 py-2.5">
        <label className="flex cursor-pointer items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={valores.contaApartada}
            onChange={(evento) => mudar({ contaApartada: evento.target.checked })}
            className={CLASSE_DA_CAIXA}
          />
          <span className="text-[12.5px] font-semibold text-foreground">
            Pagamento por conta apartada (fora do C6)
          </span>
        </label>
        <div className="mt-1.5 text-[10.5px] text-texto-fraco">
          Não gera lançamento real de folha/ajuda de custo — só aparece de forma informativa no
          Fechamento, com uma Ordem de Pagamento para quem for executar o pagamento por fora.
        </div>
        {valores.contaApartada && (
          <div className="mt-2.5">
            <LinhaDeCampos colunas={2}>
              <Campo rotulo="A débito de" id="perfil-debito-de">
                <input
                  id="perfil-debito-de"
                  placeholder="Ex: Nome do titular"
                  value={valores.debitoDe}
                  onChange={(evento) => mudar({ debitoDe: evento.target.value })}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Banco" id="perfil-banco">
                <input
                  id="perfil-banco"
                  placeholder="Ex: Itaú"
                  value={valores.banco}
                  onChange={(evento) => mudar({ banco: evento.target.value })}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
            </LinhaDeCampos>
            <LinhaDeCampos colunas={3}>
              <Campo rotulo="Agência" id="perfil-agencia">
                <input
                  id="perfil-agencia"
                  placeholder="0000"
                  value={valores.agencia}
                  onChange={(evento) => mudar({ agencia: evento.target.value })}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Conta" id="perfil-conta">
                <input
                  id="perfil-conta"
                  placeholder="00000-0"
                  value={valores.conta}
                  onChange={(evento) => mudar({ conta: evento.target.value })}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Tipo" id="perfil-tipo-conta">
                <select
                  id="perfil-tipo-conta"
                  value={valores.tipoConta}
                  onChange={(evento) =>
                    mudar({
                      tipoConta: evento.target.value === 'Poupança' ? 'Poupança' : 'Corrente',
                    })
                  }
                  className={CLASSE_DO_SELECT}
                >
                  <option>Corrente</option>
                  <option>Poupança</option>
                </select>
              </Campo>
            </LinhaDeCampos>
          </div>
        )}
      </div>

      <div className="mb-3 rounded-[6px] border border-brass/25 bg-background px-3 py-2.5">
        <label className="flex cursor-pointer items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={valores.categoriaPadrao === CATEGORIA_PESSOAL}
            onChange={(evento) =>
              mudar({
                categoriaPadrao: evento.target.checked ? CATEGORIA_PESSOAL : '',
                subcategoriaPadrao: '',
              })
            }
            className={CLASSE_DA_CAIXA}
          />
          <span className="text-[12.5px] font-semibold text-foreground">
            Definir como colaborador
          </span>
        </label>
        <div className="mt-1.5 text-[10.5px] text-texto-fraco">
          Põe a categoria padrão em Despesas com Pessoal e abre as ajudas de custo que este
          colaborador recebe.
        </div>
      </div>

      <div className="mb-3 rounded-[6px] border border-brass/25 bg-brass/7 px-3 py-[9px] text-[12px] leading-[1.5] text-brass-light">
        Defina os padrões que serão preenchidos automaticamente ao selecionar este favorecido
        num novo lançamento.
      </div>

      <LinhaDeCampos colunas={2}>
        <Campo rotulo="Categoria padrão" id="perfil-categoria">
          <select
            id="perfil-categoria"
            value={valores.categoriaPadrao}
            onChange={(evento) =>
              mudar({ categoriaPadrao: evento.target.value, subcategoriaPadrao: '' })
            }
            className={CLASSE_DO_SELECT}
          >
            <option value="">— Nenhuma —</option>
            {plano.map((categoria) => (
              <option key={categoria.id} value={categoria.nome}>
                {categoria.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Subcategoria padrão" id="perfil-subcategoria">
          <select
            id="perfil-subcategoria"
            value={valores.subcategoriaPadrao}
            onChange={(evento) => mudar({ subcategoriaPadrao: evento.target.value })}
            className={CLASSE_DO_SELECT}
          >
            <option value="">— Nenhuma —</option>
            {subcategorias.map((subcategoria) => (
              <option key={subcategoria.id} value={subcategoria.nome}>
                {subcategoria.nome}
              </option>
            ))}
          </select>
        </Campo>
      </LinhaDeCampos>
      <Campo rotulo="Forma de pagamento padrão" id="perfil-forma">
        <select
          id="perfil-forma"
          value={valores.formaPagamentoPadrao}
          onChange={(evento) =>
            mudar({ formaPagamentoPadrao: formaEscolhida(evento.target.value) })
          }
          className={CLASSE_DO_SELECT}
        >
          <option value="">— Nenhuma —</option>
          {FORMA_PAGAMENTO_LANCAMENTO.map((forma) => (
            <option key={forma} value={forma}>
              {ROTULOS_FORMA[forma]}
            </option>
          ))}
        </select>
      </Campo>

      {valores.categoriaPadrao === CATEGORIA_PESSOAL && (
        <div>
          <span className={CLASSE_DO_ROTULO_DA_SECAO}>
            Ajudas de custo habilitadas{' '}
            <span className="font-normal normal-case">(somente colaboradores)</span>
          </span>
          <div className="flex flex-col overflow-hidden rounded-[6px] border border-brass/25 bg-background">
            {LINHAS_DA_AJUDA.map(({ tipo, unidade, mascarar }) => {
              const linha = valores.ajuda[tipo];
              const id = `perfil-ajuda-${tipo}`;
              return (
                <div
                  key={tipo}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2',
                    tipo !== 'inss' && 'border-b border-brass/15',
                  )}
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={linha.marcada}
                    onChange={(evento) => mudarAjuda(tipo, { marcada: evento.target.checked })}
                    className={CLASSE_DA_CAIXA}
                  />
                  <label htmlFor={id} className="flex-1 cursor-pointer text-[13px]">
                    {ROTULOS_DA_AJUDA_DE_CUSTO[tipo]}
                  </label>
                  <input
                    aria-label={`Valor de ${ROTULOS_DA_AJUDA_DE_CUSTO[tipo]}`}
                    inputMode="decimal"
                    placeholder="0,00"
                    disabled={!linha.marcada}
                    value={linha.valor}
                    onChange={(evento) =>
                      mudarAjuda(tipo, { valor: mascarar(evento.target.value) })
                    }
                    className={CLASSE_DO_VALOR}
                  />
                  <span className="w-[38px] shrink-0 text-[10.5px] text-texto-fraco">
                    {unidade}
                  </span>
                  <span className="w-24 shrink-0 text-right text-[11.5px] font-bold text-foreground">
                    {tipo === 'custo' && totais.custo}
                    {tipo === 'diarias' && totais.diarias}
                  </span>
                </div>
              );
            })}
            <div className="flex items-center gap-2.5 border-t border-brass/20 bg-brass/8 px-3 py-1.5">
              <span className="flex-1 text-[11px] text-texto-fraco">
                Total a receber (30 dias)
              </span>
              <span className="text-[13px] font-extrabold text-foreground">
                {totais.total}
              </span>
            </div>
          </div>
          <div className="mt-2.5 rounded-[6px] border border-brass/25 bg-background px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <input
                id="perfil-alimentacao"
                type="checkbox"
                checked={valores.alimentacao.marcada}
                onChange={(evento) =>
                  mudar({
                    alimentacao: { ...valores.alimentacao, marcada: evento.target.checked },
                  })
                }
                className={CLASSE_DA_CAIXA}
              />
              <label
                htmlFor="perfil-alimentacao"
                className="flex-1 cursor-pointer text-[13px] font-semibold"
              >
                Auxílio alimentação
              </label>
              <input
                aria-label="Valor do auxílio alimentação"
                inputMode="decimal"
                placeholder="0,00"
                disabled={!valores.alimentacao.marcada}
                value={valores.alimentacao.valor}
                onChange={(evento) =>
                  mudar({
                    alimentacao: {
                      ...valores.alimentacao,
                      valor: mascararCentavos(evento.target.value),
                    },
                  })
                }
                className={CLASSE_DO_VALOR}
              />
              <span className="w-[38px] shrink-0 text-[10.5px] text-texto-fraco">/ mês</span>
            </div>
            <div className="mt-1.5 text-[10.5px] text-texto-fraco">
              Pago à parte, por transferência, na subcategoria Benefícios alimentação, com
              recibo próprio — não entra no total acima nem no recibo da ajuda de custo.
            </div>
          </div>
          <div className="mt-2.5 rounded-[6px] border border-brass/25 bg-background px-3 py-2.5">
            <label className="flex cursor-pointer items-center gap-2 select-none">
              <input
                type="checkbox"
                checked={valores.recorrente}
                onChange={(evento) => mudar({ recorrente: evento.target.checked })}
                className={CLASSE_DA_CAIXA}
              />
              <span className="text-[12.5px] font-semibold text-foreground">
                Repetir automaticamente todo mês
              </span>
            </label>
            {valores.recorrente && (
              <div className="mt-2">
                <div className="mb-1.5 text-[10.5px] text-texto-fraco">
                  A partir de qual competência gerar os lançamentos (Pendente) sozinho, sempre
                  até dezembro do ano corrente:
                </div>
                <div className="flex gap-2">
                  <select
                    aria-label="Mês da recorrência"
                    value={valores.recorrenciaMes}
                    onChange={(evento) =>
                      mudar({ recorrenciaMes: Number(evento.target.value) })
                    }
                    className={cn(CLASSE_DO_CAMPO_NATIVO, 'flex-1')}
                  >
                    {NOMES_DOS_MESES.map((nome, indice) => (
                      <option key={nome} value={indice + 1}>
                        {nome}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    aria-label="Ano da recorrência"
                    value={valores.recorrenciaAno}
                    onChange={(evento) => mudar({ recorrenciaAno: evento.target.value })}
                    className={cn(CLASSE_DO_CAMPO_NATIVO, 'w-[90px]')}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {valores.categoriaPadrao === CATEGORIA_PETRELLA && (
        <div>
          <span className={CLASSE_DO_ROTULO_DA_SECAO}>
            Diarista habilitada <span className="font-normal normal-case">(Petrella)</span>
          </span>
          <div className="rounded-[6px] border border-brass/25 bg-background px-3 py-2.5">
            <label className="flex cursor-pointer items-center gap-2 select-none">
              <input
                type="checkbox"
                checked={valores.diaristaHabilitada}
                onChange={(evento) => mudar({ diaristaHabilitada: evento.target.checked })}
                className={CLASSE_DA_CAIXA}
              />
              <span className="flex-1 text-[13px] font-semibold text-foreground">
                Habilitar diarista
              </span>
              <input
                aria-label="Valor da diária da diarista"
                inputMode="decimal"
                placeholder="0,00"
                disabled={!valores.diaristaHabilitada}
                value={valores.diaristaValor}
                onChange={(evento) =>
                  mudar({ diaristaValor: mascararDecimalLivre(evento.target.value) })
                }
                className={CLASSE_DO_VALOR}
              />
              <span className="w-[38px] shrink-0 text-[10.5px] text-texto-fraco">/ dia</span>
            </label>
            <div className="mt-1.5 text-[10.5px] text-texto-fraco">
              Sem total fixo — os dias variam mês a mês (trabalho esporádico), sempre
              preenchidos na hora do lançamento.
            </div>
          </div>
        </div>
      )}
    </CaixaDeDialogo>
  );
}
